import { c } from 'react-compiler-runtime';
import React, { useRef } from 'react';
import { clsx } from 'clsx';
import { useId } from '../hooks/useId.js';
import { useRefObjectAsForwardedRef } from '../hooks/useRefObjectAsForwardedRef.js';
import { isResponsiveValue } from '../hooks/useResponsiveValue.js';
import { useSlots } from '../hooks/useSlots.js';
import { canUseDOM } from '../utils/environment.js';
import { useOverflow } from '../hooks/useOverflow.js';
import { warning } from '../utils/warning.js';
import { getResponsiveAttributes } from '../internal/utils/getResponsiveAttributes.js';
import classes from './PageLayout.module.css.js';
import { jsxs, jsx } from 'react/jsx-runtime';

const PageLayoutContext = /*#__PURE__*/React.createContext({
  padding: 'normal',
  rowGap: 'normal',
  columnGap: 'normal',
  paneRef: {
    current: null
  }
});

// TODO: refs
const Root = t0 => {
  const $ = c(26);
  const {
    containerWidth: t1,
    padding: t2,
    rowGap: t3,
    columnGap: t4,
    children,
    className,
    style,
    _slotsConfig: slotsConfig
  } = t0;
  const containerWidth = t1 === undefined ? "xlarge" : t1;
  const padding = t2 === undefined ? "normal" : t2;
  const rowGap = t3 === undefined ? "normal" : t3;
  const columnGap = t4 === undefined ? "normal" : t4;
  const paneRef = useRef(null);
  let t5;
  if ($[0] !== slotsConfig) {
    t5 = slotsConfig !== null && slotsConfig !== void 0 ? slotsConfig : {
      header: Header,
      footer: Footer
    };
    $[0] = slotsConfig;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const [slots, rest] = useSlots(children, t5);
  let t6;
  if ($[2] !== columnGap || $[3] !== padding || $[4] !== rowGap) {
    t6 = {
      padding,
      rowGap,
      columnGap,
      paneRef
    };
    $[2] = columnGap;
    $[3] = padding;
    $[4] = rowGap;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const memoizedContextValue = t6;
  const t7 = `var(--spacing-${padding})`;
  let t8;
  if ($[6] !== style || $[7] !== t7) {
    t8 = {
      "--spacing": t7,
      ...style
    };
    $[6] = style;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const t9 = t8;
  let t10;
  if ($[9] !== className) {
    t10 = clsx(classes.PageLayoutRoot, className);
    $[9] = className;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const t11 = slots.header;
  let t12;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = clsx(classes.PageLayoutContent);
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  if ($[12] !== rest) {
    t13 = /*#__PURE__*/jsx("div", {
      className: t12,
      children: rest
    });
    $[12] = rest;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[14] !== containerWidth || $[15] !== slots.footer || $[16] !== slots.header || $[17] !== t13) {
    t14 = /*#__PURE__*/jsxs("div", {
      className: classes.PageLayoutWrapper,
      "data-width": containerWidth,
      children: [t11, t13, slots.footer]
    });
    $[14] = containerWidth;
    $[15] = slots.footer;
    $[16] = slots.header;
    $[17] = t13;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== t10 || $[20] !== t14 || $[21] !== t9) {
    t15 = /*#__PURE__*/jsx("div", {
      style: t9,
      className: t10,
      children: t14
    });
    $[19] = t10;
    $[20] = t14;
    $[21] = t9;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== memoizedContextValue || $[24] !== t15) {
    t16 = /*#__PURE__*/jsx(PageLayoutContext.Provider, {
      value: memoizedContextValue,
      children: t15
    });
    $[23] = memoizedContextValue;
    $[24] = t15;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  return t16;
};
Root.displayName = 'PageLayout';

// ----------------------------------------------------------------------------
// Divider (internal)

const HorizontalDivider = t0 => {
  const $ = c(14);
  const {
    variant: t1,
    className,
    position,
    style
  } = t0;
  const variant = t1 === undefined ? "none" : t1;
  const {
    padding
  } = React.useContext(PageLayoutContext);
  let t2;
  if ($[0] !== className) {
    t2 = clsx(classes.HorizontalDivider, className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== variant) {
    t3 = getResponsiveAttributes("variant", variant);
    $[2] = variant;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== position) {
    t4 = getResponsiveAttributes("position", position);
    $[4] = position;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = `var(--spacing-${padding})`;
  let t6;
  if ($[6] !== style || $[7] !== t5) {
    t6 = {
      "--spacing-divider": t5,
      ...style
    };
    $[6] = style;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = t6;
  let t8;
  if ($[9] !== t2 || $[10] !== t3 || $[11] !== t4 || $[12] !== t7) {
    t8 = /*#__PURE__*/jsx("div", {
      className: t2,
      ...t3,
      ...t4,
      style: t7
    });
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
};
const VerticalDivider = t0 => {
  const $ = c(40);
  const {
    variant: t1,
    draggable: t2,
    onDragStart,
    onDrag,
    onDragEnd,
    onDoubleClick,
    position,
    className,
    style
  } = t0;
  const variant = t1 === undefined ? "none" : t1;
  const draggable = t2 === undefined ? false : t2;
  const [isDragging, setIsDragging] = React.useState(false);
  const [isKeyboardDrag, setIsKeyboardDrag] = React.useState(false);
  const stableOnDrag = React.useRef(onDrag);
  const stableOnDragEnd = React.useRef(onDragEnd);
  const {
    paneRef
  } = React.useContext(PageLayoutContext);
  const [minWidth, setMinWidth] = React.useState(0);
  const [maxWidth, setMaxWidth] = React.useState(0);
  const [currentWidth, setCurrentWidth] = React.useState(0);
  let t3;
  if ($[0] !== paneRef) {
    t3 = () => {
      if (paneRef.current !== null) {
        const paneStyles = getComputedStyle(paneRef.current);
        const maxPaneWidthDiffPixels = paneStyles.getPropertyValue("--pane-max-width-diff");
        const minWidthPixels = paneStyles.getPropertyValue("--pane-min-width");
        const paneWidth = paneRef.current.getBoundingClientRect().width;
        const maxPaneWidthDiff = Number(maxPaneWidthDiffPixels.split("px")[0]);
        const minPaneWidth = Number(minWidthPixels.split("px")[0]);
        const viewportWidth = window.innerWidth;
        const maxPaneWidth = viewportWidth > maxPaneWidthDiff ? viewportWidth - maxPaneWidthDiff : viewportWidth;
        setMinWidth(minPaneWidth);
        setMaxWidth(maxPaneWidth);
        setCurrentWidth(paneWidth || 0);
      }
    };
    $[0] = paneRef;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== isDragging || $[3] !== isKeyboardDrag || $[4] !== paneRef) {
    t4 = [paneRef, isKeyboardDrag, isDragging];
    $[2] = isDragging;
    $[3] = isKeyboardDrag;
    $[4] = paneRef;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  React.useEffect(t3, t4);
  let t5;
  let t6;
  if ($[6] !== onDrag) {
    t5 = () => {
      stableOnDrag.current = onDrag;
    };
    t6 = [onDrag];
    $[6] = onDrag;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  React.useEffect(t5, t6);
  let t7;
  let t8;
  if ($[9] !== onDragEnd) {
    t7 = () => {
      stableOnDragEnd.current = onDragEnd;
    };
    t8 = [onDragEnd];
    $[9] = onDragEnd;
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  React.useEffect(t7, t8);
  let t10;
  let t9;
  if ($[12] !== currentWidth || $[13] !== isDragging || $[14] !== isKeyboardDrag || $[15] !== maxWidth || $[16] !== minWidth) {
    t9 = () => {
      const handleDrag = function handleDrag(event) {
        var _stableOnDrag$current;
        (_stableOnDrag$current = stableOnDrag.current) === null || _stableOnDrag$current === void 0 ? void 0 : _stableOnDrag$current.call(stableOnDrag, event.movementX, false);
        event.preventDefault();
      };
      const handleDragEnd = function handleDragEnd(event_0) {
        var _stableOnDragEnd$curr;
        setIsDragging(false);
        (_stableOnDragEnd$curr = stableOnDragEnd.current) === null || _stableOnDragEnd$curr === void 0 ? void 0 : _stableOnDragEnd$curr.call(stableOnDragEnd);
        event_0.preventDefault();
      };
      const handleKeyDrag = function handleKeyDrag(event_1) {
        var _stableOnDrag$current2;
        let delta;
        if ((event_1.key === "ArrowLeft" || event_1.key === "ArrowDown") && currentWidth > minWidth) {
          delta = -3;
        } else {
          if ((event_1.key === "ArrowRight" || event_1.key === "ArrowUp") && currentWidth < maxWidth) {
            delta = 3;
          } else {
            return;
          }
        }
        setCurrentWidth(currentWidth + delta);
        (_stableOnDrag$current2 = stableOnDrag.current) === null || _stableOnDrag$current2 === void 0 ? void 0 : _stableOnDrag$current2.call(stableOnDrag, delta, true);
        event_1.preventDefault();
      };
      const handleKeyDragEnd = function handleKeyDragEnd(event_2) {
        var _stableOnDragEnd$curr2;
        setIsKeyboardDrag(false);
        (_stableOnDragEnd$curr2 = stableOnDragEnd.current) === null || _stableOnDragEnd$curr2 === void 0 ? void 0 : _stableOnDragEnd$curr2.call(stableOnDragEnd);
        event_2.preventDefault();
      };
      if (isDragging || isKeyboardDrag) {
        window.addEventListener("mousemove", handleDrag);
        window.addEventListener("keydown", handleKeyDrag);
        window.addEventListener("mouseup", handleDragEnd);
        window.addEventListener("keyup", handleKeyDragEnd);
        const body = document.body;
        body === null || body === void 0 ? void 0 : body.setAttribute("data-page-layout-dragging", "true");
      } else {
        window.removeEventListener("mousemove", handleDrag);
        window.removeEventListener("mouseup", handleDragEnd);
        window.removeEventListener("keydown", handleKeyDrag);
        window.removeEventListener("keyup", handleKeyDragEnd);
        const body_0 = document.body;
        body_0 === null || body_0 === void 0 ? void 0 : body_0.removeAttribute("data-page-layout-dragging");
      }
      return () => {
        window.removeEventListener("mousemove", handleDrag);
        window.removeEventListener("mouseup", handleDragEnd);
        window.removeEventListener("keydown", handleKeyDrag);
        window.removeEventListener("keyup", handleKeyDragEnd);
        const body_1 = document.body;
        body_1 === null || body_1 === void 0 ? void 0 : body_1.removeAttribute("data-page-layout-dragging");
      };
    };
    t10 = [isDragging, isKeyboardDrag, currentWidth, minWidth, maxWidth];
    $[12] = currentWidth;
    $[13] = isDragging;
    $[14] = isKeyboardDrag;
    $[15] = maxWidth;
    $[16] = minWidth;
    $[17] = t10;
    $[18] = t9;
  } else {
    t10 = $[17];
    t9 = $[18];
  }
  React.useEffect(t9, t10);
  let t11;
  if ($[19] !== className) {
    t11 = clsx(classes.VerticalDivider, className);
    $[19] = className;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== variant) {
    t12 = getResponsiveAttributes("variant", variant);
    $[21] = variant;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== position) {
    t13 = getResponsiveAttributes("position", position);
    $[23] = position;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== currentWidth || $[26] !== draggable || $[27] !== isDragging || $[28] !== isKeyboardDrag || $[29] !== maxWidth || $[30] !== minWidth || $[31] !== onDoubleClick || $[32] !== onDragStart) {
    t14 = draggable ? /*#__PURE__*/jsx("div", {
      className: classes.DraggableHandle,
      "data-dragging": isDragging || isKeyboardDrag,
      role: "slider",
      "aria-label": "Draggable pane splitter",
      "aria-valuemin": minWidth,
      "aria-valuemax": maxWidth,
      "aria-valuenow": currentWidth,
      "aria-valuetext": `Pane width ${currentWidth} pixels`,
      tabIndex: 0,
      onMouseDown: event_3 => {
        if (event_3.button === 0) {
          setIsDragging(true);
          onDragStart === null || onDragStart === void 0 ? void 0 : onDragStart();
        }
      },
      onKeyDown: event_4 => {
        if (event_4.key === "ArrowLeft" || event_4.key === "ArrowRight" || event_4.key === "ArrowUp" || event_4.key === "ArrowDown") {
          setIsKeyboardDrag(true);
          onDragStart === null || onDragStart === void 0 ? void 0 : onDragStart();
        }
      },
      onDoubleClick: onDoubleClick
    }) : null;
    $[25] = currentWidth;
    $[26] = draggable;
    $[27] = isDragging;
    $[28] = isKeyboardDrag;
    $[29] = maxWidth;
    $[30] = minWidth;
    $[31] = onDoubleClick;
    $[32] = onDragStart;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== style || $[35] !== t11 || $[36] !== t12 || $[37] !== t13 || $[38] !== t14) {
    t15 = /*#__PURE__*/jsx("div", {
      className: t11,
      ...t12,
      ...t13,
      style: style,
      children: t14
    });
    $[34] = style;
    $[35] = t11;
    $[36] = t12;
    $[37] = t13;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  return t15;
};

// ----------------------------------------------------------------------------
// PageLayout.Header

const Header = t0 => {
  const $ = c(28);
  const {
    "aria-label": label,
    "aria-labelledby": labelledBy,
    padding: t1,
    divider: t2,
    dividerWhenNarrow: t3,
    hidden: t4,
    children,
    style,
    className
  } = t0;
  const padding = t1 === undefined ? "none" : t1;
  const divider = t2 === undefined ? "none" : t2;
  const dividerWhenNarrow = t3 === undefined ? "inherit" : t3;
  const hidden = t4 === undefined ? false : t4;
  let t5;
  if ($[0] !== divider || $[1] !== dividerWhenNarrow) {
    t5 = !isResponsiveValue(divider) && dividerWhenNarrow !== "inherit" ? {
      regular: divider,
      narrow: dividerWhenNarrow
    } : divider;
    $[0] = divider;
    $[1] = dividerWhenNarrow;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const dividerProp = t5;
  const {
    rowGap
  } = React.useContext(PageLayoutContext);
  let t6;
  if ($[3] !== hidden) {
    t6 = getResponsiveAttributes("hidden", hidden);
    $[3] = hidden;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== className) {
    t7 = clsx(classes.Header, className);
    $[5] = className;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const t8 = `var(--spacing-${rowGap})`;
  let t9;
  if ($[7] !== style || $[8] !== t8) {
    t9 = {
      "--spacing": t8,
      ...style
    };
    $[7] = style;
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const t10 = t9;
  const t11 = `var(--spacing-${padding})`;
  let t12;
  if ($[10] !== t11) {
    t12 = {
      "--spacing": t11
    };
    $[10] = t11;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  const t13 = t12;
  let t14;
  if ($[12] !== children || $[13] !== t13) {
    t14 = /*#__PURE__*/jsx("div", {
      className: classes.HeaderContent,
      style: t13,
      children: children
    });
    $[12] = children;
    $[13] = t13;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  const t15 = `var(--spacing-${rowGap})`;
  let t16;
  if ($[15] !== t15) {
    t16 = {
      "--spacing": t15
    };
    $[15] = t15;
    $[16] = t16;
  } else {
    t16 = $[16];
  }
  const t17 = t16;
  let t18;
  if ($[17] !== dividerProp || $[18] !== t17) {
    t18 = /*#__PURE__*/jsx(HorizontalDivider, {
      variant: dividerProp,
      className: classes.HeaderHorizontalDivider,
      style: t17
    });
    $[17] = dividerProp;
    $[18] = t17;
    $[19] = t18;
  } else {
    t18 = $[19];
  }
  let t19;
  if ($[20] !== label || $[21] !== labelledBy || $[22] !== t10 || $[23] !== t14 || $[24] !== t18 || $[25] !== t6 || $[26] !== t7) {
    t19 = /*#__PURE__*/jsxs("header", {
      "aria-label": label,
      "aria-labelledby": labelledBy,
      ...t6,
      className: t7,
      style: t10,
      children: [t14, t18]
    });
    $[20] = label;
    $[21] = labelledBy;
    $[22] = t10;
    $[23] = t14;
    $[24] = t18;
    $[25] = t6;
    $[26] = t7;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  return t19;
};
Header.displayName = 'PageLayout.Header';
const Content = t0 => {
  const $ = c(18);
  const {
    as: t1,
    "aria-label": label,
    "aria-labelledby": labelledBy,
    width: t2,
    padding: t3,
    hidden: t4,
    children,
    className,
    style
  } = t0;
  const as = t1 === undefined ? "main" : t1;
  const width = t2 === undefined ? "full" : t2;
  const padding = t3 === undefined ? "none" : t3;
  const hidden = t4 === undefined ? false : t4;
  const Component = as;
  let t5;
  if ($[0] !== className) {
    t5 = clsx(classes.ContentWrapper, className);
    $[0] = className;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  let t6;
  if ($[2] !== hidden) {
    t6 = getResponsiveAttributes("is-hidden", hidden);
    $[2] = hidden;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const t7 = `var(--spacing-${padding})`;
  let t8;
  if ($[4] !== t7) {
    t8 = {
      "--spacing": t7
    };
    $[4] = t7;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const t9 = t8;
  let t10;
  if ($[6] !== children || $[7] !== t9 || $[8] !== width) {
    t10 = /*#__PURE__*/jsx("div", {
      className: classes.Content,
      "data-width": width,
      style: t9,
      children: children
    });
    $[6] = children;
    $[7] = t9;
    $[8] = width;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== Component || $[11] !== label || $[12] !== labelledBy || $[13] !== style || $[14] !== t10 || $[15] !== t5 || $[16] !== t6) {
    t11 = /*#__PURE__*/jsx(Component, {
      "aria-label": label,
      "aria-labelledby": labelledBy,
      style: style,
      className: t5,
      ...t6,
      children: t10
    });
    $[10] = Component;
    $[11] = label;
    $[12] = labelledBy;
    $[13] = style;
    $[14] = t10;
    $[15] = t5;
    $[16] = t6;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  return t11;
};
Content.displayName = 'PageLayout.Content';

// ----------------------------------------------------------------------------
// PageLayout.Pane

const isCustomWidthOptions = width => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  return width.default !== undefined;
};
const isPaneWidth = width => {
  return ['small', 'medium', 'large'].includes(width);
};
const defaultPaneWidth = {
  small: 256,
  medium: 296,
  large: 320
};
const overflowProps = {
  tabIndex: 0,
  role: 'region'
};
const Pane = /*#__PURE__*/React.forwardRef((t0, forwardRef) => {
  const $ = c(92);
  const {
    "aria-label": label,
    "aria-labelledby": labelledBy,
    position: t1,
    positionWhenNarrow: t2,
    width: t3,
    minWidth: t4,
    padding: t5,
    resizable: t6,
    widthStorageKey: t7,
    divider: t8,
    dividerWhenNarrow: t9,
    sticky: t10,
    offsetHeader: t11,
    hidden: t12,
    children,
    id,
    className,
    style
  } = t0;
  const responsivePosition = t1 === undefined ? "end" : t1;
  const positionWhenNarrow = t2 === undefined ? "inherit" : t2;
  const width = t3 === undefined ? "medium" : t3;
  const minWidth = t4 === undefined ? 256 : t4;
  const padding = t5 === undefined ? "none" : t5;
  const resizable = t6 === undefined ? false : t6;
  const widthStorageKey = t7 === undefined ? "paneWidth" : t7;
  const responsiveDivider = t8 === undefined ? "none" : t8;
  const dividerWhenNarrow = t9 === undefined ? "inherit" : t9;
  const sticky = t10 === undefined ? false : t10;
  const offsetHeader = t11 === undefined ? 0 : t11;
  const responsiveHidden = t12 === undefined ? false : t12;
  let t13;
  if ($[0] !== positionWhenNarrow || $[1] !== responsivePosition) {
    t13 = !isResponsiveValue(responsivePosition) && positionWhenNarrow !== "inherit" ? {
      regular: responsivePosition,
      narrow: positionWhenNarrow
    } : responsivePosition;
    $[0] = positionWhenNarrow;
    $[1] = responsivePosition;
    $[2] = t13;
  } else {
    t13 = $[2];
  }
  const positionProp = t13;
  let t14;
  if ($[3] !== dividerWhenNarrow || $[4] !== responsiveDivider) {
    t14 = !isResponsiveValue(responsiveDivider) && dividerWhenNarrow !== "inherit" ? {
      regular: responsiveDivider,
      narrow: dividerWhenNarrow
    } : responsiveDivider;
    $[3] = dividerWhenNarrow;
    $[4] = responsiveDivider;
    $[5] = t14;
  } else {
    t14 = $[5];
  }
  const dividerProp = t14;
  let t15;
  if ($[6] !== positionProp) {
    t15 = isResponsiveValue(positionProp) ? "end" : positionProp;
    $[6] = positionProp;
    $[7] = t15;
  } else {
    t15 = $[7];
  }
  const position = t15;
  let t16;
  if ($[8] !== dividerProp) {
    t16 = isResponsiveValue(dividerProp) ? "none" : dividerProp;
    $[8] = dividerProp;
    $[9] = t16;
  } else {
    t16 = $[9];
  }
  const dividerVariant = t16;
  const {
    rowGap,
    columnGap,
    paneRef
  } = React.useContext(PageLayoutContext);
  const getDefaultPaneWidth = _temp;
  let t17;
  if ($[10] !== width || $[11] !== widthStorageKey) {
    t17 = () => {
      if (!canUseDOM) {
        return getDefaultPaneWidth(width);
      }
      let storedWidth;
      try {
        storedWidth = localStorage.getItem(widthStorageKey);
      } catch (t18) {
        storedWidth = null;
      }
      return storedWidth && !isNaN(Number(storedWidth)) ? Number(storedWidth) : getDefaultPaneWidth(width);
    };
    $[10] = width;
    $[11] = widthStorageKey;
    $[12] = t17;
  } else {
    t17 = $[12];
  }
  const [paneWidth, setPaneWidth] = React.useState(t17);
  let t18;
  if ($[13] !== widthStorageKey) {
    t18 = width_1 => {
      setPaneWidth(width_1);
      try {
        localStorage.setItem(widthStorageKey, width_1.toString());
      } catch (t19) {
      }
    };
    $[13] = widthStorageKey;
    $[14] = t18;
  } else {
    t18 = $[14];
  }
  const updatePaneWidth = t18;
  useRefObjectAsForwardedRef(forwardRef, paneRef);
  const hasOverflow = useOverflow(paneRef);
  const paneId = useId(id);
  let labelProp;
  if ($[15] !== hasOverflow || $[16] !== label || $[17] !== labelledBy) {
    labelProp = {};
    if (hasOverflow) {
      process.env.NODE_ENV !== "production" ? warning(label === undefined && labelledBy === undefined, "The <PageLayout.Pane> has overflow and `aria-label` or `aria-labelledby` has not been set. Please provide `aria-label` or `aria-labelledby` to <PageLayout.Pane> in order to label this region.") : void 0;
      if (labelledBy) {
        labelProp["aria-labelledby"] = labelledBy;
      } else {
        if (label) {
          labelProp["aria-label"] = label;
        }
      }
    }
    $[15] = hasOverflow;
    $[16] = label;
    $[17] = labelledBy;
    $[18] = labelProp;
  } else {
    labelProp = $[18];
  }
  let t19;
  if ($[19] !== className) {
    t19 = clsx(classes.PaneWrapper, className);
    $[19] = className;
    $[20] = t19;
  } else {
    t19 = $[20];
  }
  const t20 = typeof offsetHeader === "number" ? `${offsetHeader}px` : offsetHeader;
  const t21 = `var(--spacing-${rowGap})`;
  const t22 = `var(--spacing-${columnGap})`;
  let t23;
  if ($[21] !== style || $[22] !== t20 || $[23] !== t21 || $[24] !== t22) {
    t23 = {
      "--offset-header": t20,
      "--spacing-row": t21,
      "--spacing-column": t22,
      ...style
    };
    $[21] = style;
    $[22] = t20;
    $[23] = t21;
    $[24] = t22;
    $[25] = t23;
  } else {
    t23 = $[25];
  }
  const t24 = t23;
  let t25;
  if ($[26] !== responsiveHidden) {
    t25 = getResponsiveAttributes("is-hidden", responsiveHidden);
    $[26] = responsiveHidden;
    $[27] = t25;
  } else {
    t25 = $[27];
  }
  let t26;
  if ($[28] !== positionProp) {
    t26 = getResponsiveAttributes("position", positionProp);
    $[28] = positionProp;
    $[29] = t26;
  } else {
    t26 = $[29];
  }
  const t27 = sticky || undefined;
  let t28;
  if ($[30] !== dividerProp || $[31] !== dividerVariant) {
    t28 = isResponsiveValue(dividerProp) ? dividerProp : {
      narrow: dividerVariant,
      regular: "none"
    };
    $[30] = dividerProp;
    $[31] = dividerVariant;
    $[32] = t28;
  } else {
    t28 = $[32];
  }
  const t29 = `var(--spacing-${rowGap})`;
  let t30;
  if ($[33] !== t29) {
    t30 = {
      "--spacing": t29
    };
    $[33] = t29;
    $[34] = t30;
  } else {
    t30 = $[34];
  }
  const t31 = t30;
  let t32;
  if ($[35] !== positionProp || $[36] !== t28 || $[37] !== t31) {
    t32 = /*#__PURE__*/jsx(HorizontalDivider, {
      variant: t28,
      className: classes.PaneHorizontalDivider,
      style: t31,
      position: positionProp
    });
    $[35] = positionProp;
    $[36] = t28;
    $[37] = t31;
    $[38] = t32;
  } else {
    t32 = $[38];
  }
  let t33;
  if ($[39] !== hasOverflow) {
    t33 = hasOverflow ? overflowProps : {};
    $[39] = hasOverflow;
    $[40] = t33;
  } else {
    t33 = $[40];
  }
  let t34;
  if ($[41] !== id || $[42] !== paneId) {
    t34 = id && {
      id: paneId
    };
    $[41] = id;
    $[42] = paneId;
    $[43] = t34;
  } else {
    t34 = $[43];
  }
  const t35 = resizable || undefined;
  const t36 = `var(--spacing-${padding})`;
  const t37 = isCustomWidthOptions(width) ? width.min : `${minWidth}px`;
  const t38 = isCustomWidthOptions(width) ? width.max : "calc(100vw - var(--pane-max-width-diff))";
  const t39 = isCustomWidthOptions(width) ? width.default : undefined;
  const t40 = `var(--pane-width-${isPaneWidth(width) ? width : "custom"})`;
  const t41 = `${paneWidth}px`;
  let t42;
  if ($[44] !== t36 || $[45] !== t37 || $[46] !== t38 || $[47] !== t39 || $[48] !== t40 || $[49] !== t41) {
    t42 = {
      "--spacing": t36,
      "--pane-min-width": t37,
      "--pane-max-width": t38,
      "--pane-width-custom": t39,
      "--pane-width-size": t40,
      "--pane-width": t41
    };
    $[44] = t36;
    $[45] = t37;
    $[46] = t38;
    $[47] = t39;
    $[48] = t40;
    $[49] = t41;
    $[50] = t42;
  } else {
    t42 = $[50];
  }
  const t43 = t42;
  let t44;
  if ($[51] !== children || $[52] !== labelProp || $[53] !== paneRef || $[54] !== t33 || $[55] !== t34 || $[56] !== t35 || $[57] !== t43) {
    t44 = /*#__PURE__*/jsx("div", {
      ref: paneRef,
      ...t33,
      ...labelProp,
      ...t34,
      className: classes.Pane,
      "data-resizable": t35,
      style: t43,
      children: children
    });
    $[51] = children;
    $[52] = labelProp;
    $[53] = paneRef;
    $[54] = t33;
    $[55] = t34;
    $[56] = t35;
    $[57] = t43;
    $[58] = t44;
  } else {
    t44 = $[58];
  }
  let t45;
  if ($[59] !== dividerProp || $[60] !== dividerVariant || $[61] !== resizable) {
    t45 = isResponsiveValue(dividerProp) ? {
      narrow: "none",
      regular: resizable ? "line" : dividerProp.regular || "none",
      wide: resizable ? "line" : dividerProp.wide || dividerProp.regular || "none"
    } : {
      narrow: "none",
      regular: resizable ? "line" : dividerVariant
    };
    $[59] = dividerProp;
    $[60] = dividerVariant;
    $[61] = resizable;
    $[62] = t45;
  } else {
    t45 = $[62];
  }
  let t46;
  if ($[63] !== paneWidth || $[64] !== position || $[65] !== updatePaneWidth) {
    t46 = (delta, t47) => {
      const isKeyboard = t47 === undefined ? false : t47;
      let deltaWithDirection;
      if (isKeyboard) {
        deltaWithDirection = delta;
      } else {
        deltaWithDirection = position === "end" ? -delta : delta;
      }
      updatePaneWidth(paneWidth + deltaWithDirection);
    };
    $[63] = paneWidth;
    $[64] = position;
    $[65] = updatePaneWidth;
    $[66] = t46;
  } else {
    t46 = $[66];
  }
  let t47;
  if ($[67] !== paneRef || $[68] !== updatePaneWidth) {
    t47 = () => {
      var _paneRef$current;
      const paneRect = (_paneRef$current = paneRef.current) === null || _paneRef$current === void 0 ? void 0 : _paneRef$current.getBoundingClientRect();
      if (!paneRect) {
        return;
      }
      updatePaneWidth(paneRect.width);
    };
    $[67] = paneRef;
    $[68] = updatePaneWidth;
    $[69] = t47;
  } else {
    t47 = $[69];
  }
  let t48;
  if ($[70] !== updatePaneWidth || $[71] !== width) {
    t48 = () => updatePaneWidth(getDefaultPaneWidth(width));
    $[70] = updatePaneWidth;
    $[71] = width;
    $[72] = t48;
  } else {
    t48 = $[72];
  }
  const t49 = `var(--spacing-${columnGap})`;
  let t50;
  if ($[73] !== t49) {
    t50 = {
      "--spacing": t49
    };
    $[73] = t49;
    $[74] = t50;
  } else {
    t50 = $[74];
  }
  const t51 = t50;
  let t52;
  if ($[75] !== positionProp || $[76] !== resizable || $[77] !== t45 || $[78] !== t46 || $[79] !== t47 || $[80] !== t48 || $[81] !== t51) {
    t52 = /*#__PURE__*/jsx(VerticalDivider, {
      variant: t45,
      draggable: resizable,
      onDrag: t46,
      onDragEnd: t47,
      position: positionProp,
      onDoubleClick: t48,
      className: classes.PaneVerticalDivider,
      style: t51
    });
    $[75] = positionProp;
    $[76] = resizable;
    $[77] = t45;
    $[78] = t46;
    $[79] = t47;
    $[80] = t48;
    $[81] = t51;
    $[82] = t52;
  } else {
    t52 = $[82];
  }
  let t53;
  if ($[83] !== t19 || $[84] !== t24 || $[85] !== t25 || $[86] !== t26 || $[87] !== t27 || $[88] !== t32 || $[89] !== t44 || $[90] !== t52) {
    t53 = /*#__PURE__*/jsxs("div", {
      className: t19,
      style: t24,
      ...t25,
      ...t26,
      "data-sticky": t27,
      children: [t32, t44, t52]
    });
    $[83] = t19;
    $[84] = t24;
    $[85] = t25;
    $[86] = t26;
    $[87] = t27;
    $[88] = t32;
    $[89] = t44;
    $[90] = t52;
    $[91] = t53;
  } else {
    t53 = $[91];
  }
  return t53;
});
Pane.displayName = 'PageLayout.Pane';

// ----------------------------------------------------------------------------
// PageLayout.Footer

const Footer = t0 => {
  const $ = c(28);
  const {
    "aria-label": label,
    "aria-labelledby": labelledBy,
    padding: t1,
    divider: t2,
    dividerWhenNarrow: t3,
    hidden: t4,
    children,
    className,
    style
  } = t0;
  const padding = t1 === undefined ? "none" : t1;
  const divider = t2 === undefined ? "none" : t2;
  const dividerWhenNarrow = t3 === undefined ? "inherit" : t3;
  const hidden = t4 === undefined ? false : t4;
  let t5;
  if ($[0] !== divider || $[1] !== dividerWhenNarrow) {
    t5 = !isResponsiveValue(divider) && dividerWhenNarrow !== "inherit" ? {
      regular: divider,
      narrow: dividerWhenNarrow
    } : divider;
    $[0] = divider;
    $[1] = dividerWhenNarrow;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const dividerProp = t5;
  const {
    rowGap
  } = React.useContext(PageLayoutContext);
  let t6;
  if ($[3] !== hidden) {
    t6 = getResponsiveAttributes("hidden", hidden);
    $[3] = hidden;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== className) {
    t7 = clsx(classes.FooterWrapper, className);
    $[5] = className;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const t8 = `var(--spacing-${rowGap})`;
  let t9;
  if ($[7] !== style || $[8] !== t8) {
    t9 = {
      "--spacing": t8,
      ...style
    };
    $[7] = style;
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const t10 = t9;
  const t11 = `var(--spacing-${rowGap})`;
  let t12;
  if ($[10] !== t11) {
    t12 = {
      "--spacing": t11
    };
    $[10] = t11;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  const t13 = t12;
  let t14;
  if ($[12] !== dividerProp || $[13] !== t13) {
    t14 = /*#__PURE__*/jsx(HorizontalDivider, {
      className: classes.FooterHorizontalDivider,
      style: t13,
      variant: dividerProp
    });
    $[12] = dividerProp;
    $[13] = t13;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  const t15 = `var(--spacing-${padding})`;
  let t16;
  if ($[15] !== t15) {
    t16 = {
      "--spacing": t15
    };
    $[15] = t15;
    $[16] = t16;
  } else {
    t16 = $[16];
  }
  const t17 = t16;
  let t18;
  if ($[17] !== children || $[18] !== t17) {
    t18 = /*#__PURE__*/jsx("div", {
      className: classes.FooterContent,
      style: t17,
      children: children
    });
    $[17] = children;
    $[18] = t17;
    $[19] = t18;
  } else {
    t18 = $[19];
  }
  let t19;
  if ($[20] !== label || $[21] !== labelledBy || $[22] !== t10 || $[23] !== t14 || $[24] !== t18 || $[25] !== t6 || $[26] !== t7) {
    t19 = /*#__PURE__*/jsxs("footer", {
      "aria-label": label,
      "aria-labelledby": labelledBy,
      ...t6,
      className: t7,
      style: t10,
      children: [t14, t18]
    });
    $[20] = label;
    $[21] = labelledBy;
    $[22] = t10;
    $[23] = t14;
    $[24] = t18;
    $[25] = t6;
    $[26] = t7;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  return t19;
};
Footer.displayName = 'PageLayout.Footer';

// ----------------------------------------------------------------------------
// Export

const PageLayout = Object.assign(Root, {
  __SLOT__: Symbol('PageLayout'),
  Header,
  Content,
  Pane: Pane,
  Footer
});
Header.__SLOT__ = Symbol('PageLayout.Header');
Content.__SLOT__ = Symbol('PageLayout.Content');
Pane.__SLOT__ = Symbol('PageLayout.Pane');
Footer.__SLOT__ = Symbol('PageLayout.Footer');
function _temp(width_0) {
  if (isPaneWidth(width_0)) {
    return defaultPaneWidth[width_0];
  } else {
    if (isCustomWidthOptions(width_0)) {
      return Number(width_0.default.split("px")[0]);
    }
  }
  return 0;
}

export { PageLayout };
